import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { LogOut, X } from 'lucide-react';

import type { AppMode } from '../contexts/UserPreferencesContext';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

import styles from './NavDrawer.module.css';

export type NavItem = { label: string; path: string; action?: 'bug-report' };

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
  user: { name: string; email: string; photoUrl?: string };
  modeOptions: { value: AppMode; label: string }[];
  appMode: AppMode;
  onModeChange: (mode: AppMode) => void;
  navItems: NavItem[];
  isActive: (path: string) => boolean;
  onNavItem: (item: NavItem) => void;
  onLogout: () => void;
  /** Extra admin-only actions (e.g. impersonation) rendered above logout. */
  adminExtras?: ReactNode;
}

export default function NavDrawer({
  open,
  onClose,
  user,
  modeOptions,
  appMode,
  onModeChange,
  navItems,
  isActive,
  onNavItem,
  onLogout,
  adminExtras,
}: NavDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const reduceMotion = useReducedMotion();

  // Portal target (document.body) only exists on the client — SSG renders nothing.
  useEffect(() => setMounted(true), []);

  useBodyScrollLock(open);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && drawerRef.current) {
        const focusable = Array.from(
          drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose]
  );

  // Save focus on open, restore it to the trigger on close. Keyed only on `open`
  // so re-renders (which recreate handleKeyDown) don't steal focus back.
  useEffect(() => {
    if (!open) return;
    const previousActive = document.activeElement as HTMLElement | null;
    return () => previousActive?.focus?.();
  }, [open]);

  // Escape + focus-trap key handling while open.
  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  // Move focus into the drawer when it opens.
  useEffect(() => {
    if (open && drawerRef.current) {
      drawerRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
    }
  }, [open]);

  if (!mounted) return null;

  const initial = (user.name || user.email || 'U').trim()[0]?.toUpperCase() || 'U';

  const handleMode = (mode: AppMode) => {
    onModeChange(mode);
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className={styles.scrim}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.2 }}
        >
          <motion.div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navegação"
            className={styles.drawer}
            onClick={(e) => e.stopPropagation()}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={
              reduceMotion ? { duration: 0 } : { type: 'tween', duration: 0.25, ease: [0.4, 0, 0.2, 1] }
            }
          >
            <div className={styles.header}>
              <div className={styles.identity}>
                {user.photoUrl ? (
                  <img src={user.photoUrl} alt="" className={styles.avatar} />
                ) : (
                  <div className={styles.avatarFallback}>{initial}</div>
                )}
                <div className={styles.identityText}>
                  <span className={styles.identityName}>{user.name || 'Usuário'}</span>
                  <span className={styles.identityEmail}>{user.email}</span>
                </div>
              </div>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={onClose}
                aria-label="Fechar menu"
              >
                <X size={20} aria-hidden />
              </button>
            </div>

            <div className={styles.scrollArea}>
              <div className={styles.modeSelector} role="group" aria-label="Modo do aplicativo">
                {modeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${styles.modeBtn} ${appMode === opt.value ? styles.modeBtnActive : ''}`}
                    onClick={() => handleMode(opt.value)}
                    aria-pressed={appMode === opt.value}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <nav className={styles.navList} aria-label="Navegação principal">
                {navItems.map((item) => {
                  const active = item.action ? false : isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => onNavItem(item)}
                      className={`${styles.navLink} ${active ? styles.navLinkActive : ''}`}
                      aria-current={active ? 'page' : undefined}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className={styles.footer}>
              {adminExtras}
              <button
                type="button"
                className={`${styles.navLink} ${styles.logoutBtn}`}
                onClick={onLogout}
              >
                <LogOut size={18} aria-hidden />
                Sair
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
